import "./dashboard-sections.css";
import DashboardCard from "./DashboardCard";
import { formatNumber, makePriorLotNumber } from "../../../helpers/Utils";
import { useGetProcessingAnalyticsQuery } from "../dashBoardApi";
import { useEffect } from "react";
import useTranslator from "../../../hooks/useTranslator";

const MetricBox = ({ label, value, unit, isLoading = false }) => (
  <div className="db-evap-box">
    <div className="db-evap-label">{label}</div>
    <div className="db-evap-value neutral">
      {isLoading
        ? <span className="db-shimmer" style={{ height: 24, width: 60, display: "block" }} />
        : <>{value ?? "—"}{unit && <span className="db-metric-unit"> {unit}</span>}</>
      }
    </div>
  </div>
);

const BatchBadge = ({ startDate, endDate }) => (
  <span className="pko-batch-badge">
    #{makePriorLotNumber(startDate)} - #{makePriorLotNumber(endDate)}
  </span>
);

const ProcessingOverview = ({ startDate, endDate }) => {
  const { _t } = useTranslator();

  const {
    data: procsData,
    isLoading: isProcsDataLoading,
    refetch: processingRefetch,
  } = useGetProcessingAnalyticsQuery(
    {
      startDate,
      endDate,
      batchFrom: makePriorLotNumber(startDate),
      batchTo: makePriorLotNumber(endDate),
    },
    { skip: !startDate || !endDate }
  );

  useEffect(() => {
    processingRefetch();
  }, []);

  const takenOffData   = procsData?.data?.kg_taken_off ?? [];
  const trashData      = procsData?.data?.trash ?? [];
  const treysData      = procsData?.data?.treys ?? [];
  const procsCasesData = procsData?.data?.cases ?? [];

  return (
    <div className="po-wrapper">
      {/* CASES */}
      <DashboardCard
        title={_t("cases_db", "Cases")}
        badgeIcon={startDate && endDate ? <BatchBadge startDate={startDate} endDate={endDate} /> : null}
      >
        <div className="db-evap-row">
          <MetricBox label={_t("cases_entered_db", "CASES ENTERED")}   value={formatNumber(procsCasesData?.cases_entered) ?? 0}       isLoading={isProcsDataLoading} />
          <MetricBox label={_t("cases_processed_db", "CASES PROCESSED")} value={formatNumber(procsCasesData?.cases_processed) ?? 0}   isLoading={isProcsDataLoading} />
          <MetricBox label={_t("cases_combined_db", "CASES COMBINED")}  value={formatNumber(procsCasesData?.cases_combined) ?? 0}     isLoading={isProcsDataLoading} />
          <MetricBox label={_t("peelers_db", "PEELERS")}               value={formatNumber(procsCasesData?.number_of_peelers) ?? 0}   isLoading={isProcsDataLoading} />
        </div>
      </DashboardCard>

      <div className="po-bottom-row-3">

        {/* Treys & Carts */}
        <DashboardCard
          title={_t("treys_and_carts_db", "Treys & Carts")}
          badgeIcon={startDate && endDate ? <BatchBadge startDate={startDate} endDate={endDate} /> : null}
        >
          <div className="po-treys-grid">
            <MetricBox label={_t("treys_strips_db", "TREYS STRIPS")} value={formatNumber(treysData?.striped_treys , 0) ?? 0}  isLoading={isProcsDataLoading} />
            <MetricBox label={_t("treys_cheeks_db", "TREYS CHEEKS")} value={formatNumber(treysData?.cheeks_treys , 0) ?? 0}   isLoading={isProcsDataLoading} />
            <MetricBox label={_t("treys_tapita_db", "TREYS TAPITA")} value={formatNumber(treysData?.tapitas_treys , 0) ?? 0}  isLoading={isProcsDataLoading} />
            <MetricBox label={_t("total_treys_db", "TOTAL TREYS")}   value={
              formatNumber((treysData?.striped_treys ?? 0) +
              (treysData?.cheeks_treys ?? 0) +
              (treysData?.tapitas_treys ?? 0) , 0)
            } isLoading={isProcsDataLoading} />
            <MetricBox label={_t("carts_db", "CARTS")} value={formatNumber(treysData?.carts) ?? 0} isLoading={isProcsDataLoading} />
          </div>
        </DashboardCard>

        {/* KG Taken Off + Trash */}
        <div className="po-right-col">
          <DashboardCard
            title={_t("kg_taken_off_db", "KG Taken Off")}
            icon={<span className="po-total-kg-badge">{_t("total_db", "Total")}: {formatNumber(takenOffData?.total_taken_off_kg) ?? "0"} kg</span>}
            badgeIcon={startDate && endDate ? <BatchBadge startDate={startDate} endDate={endDate} /> : null}
          >
            <div className="db-avg-row">
              <span>{_t("strip_total_db", "Strip Total")}</span>
              <span className="db-avg-val">
                {isProcsDataLoading ? <span className="db-shimmer" style={{ height: 16, width: 60, display: "inline-block" }} /> : <>{formatNumber(takenOffData?.total_strips_kg) ?? "0"} <span className="db-metric-unit">kg</span></>}
              </span>
            </div>
            <div className="db-avg-row">
              <span>{_t("cheek_total_db", "Cheek Total")}</span>
              <span className="db-avg-val">
                {isProcsDataLoading ? <span className="db-shimmer" style={{ height: 16, width: 60, display: "inline-block" }} /> : <>{formatNumber(takenOffData?.total_cheeks_kg) ?? "0"} <span className="db-metric-unit">kg</span></>}
              </span>
            </div>
            <div className="db-avg-row">
              <span>{_t("tapita_total_db", "Tapita Total")}</span>
              <span className="db-avg-val">
                {isProcsDataLoading ? <span className="db-shimmer" style={{ height: 16, width: 60, display: "inline-block" }} /> : <>{formatNumber(takenOffData?.total_tapita_kg) ?? "0"} <span className="db-metric-unit">kg</span></>}
              </span>
            </div>
          </DashboardCard>

          <DashboardCard
            title={_t("trash_db", "Trash")}
            badgeIcon={startDate && endDate ? <BatchBadge startDate={startDate} endDate={endDate} /> : null}
          >
            <div className="db-avg-row">
              <span>{_t("trash_sorting_db", "Trash (Sorting)")}</span>
              <span className="db-avg-val">
                {isProcsDataLoading ? <span className="db-shimmer" style={{ height: 16, width: 60, display: "inline-block" }} /> : <>{formatNumber(trashData?.trash_kg_sorting) ?? "0"} <span className="db-metric-unit">kg</span></>}
              </span>
            </div>
            <div className="db-avg-row">
              <span>{_t("trash_processing_db", "Trash (Processing)")}</span>
              <span className="db-avg-val">
                {isProcsDataLoading ? <span className="db-shimmer" style={{ height: 16, width: 60, display: "inline-block" }} /> : <>{formatNumber(trashData?.trash_kg_processing) ?? "0"} <span className="db-metric-unit">kg</span></>}
              </span>
            </div>
          </DashboardCard>
        </div>

      </div>
    </div>
  );
};

export default ProcessingOverview;
